import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading, DownloadButton } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { ReturnForm } from "@/components/returns/ReturnForm";
import { RetPill, DaysCell, WarnChips, RetLink, orderOf, useRetMeta, opts } from "@/components/returns/RetCommon";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api } from "@/lib/api";
import { dateLabel, fmtNum } from "@/lib/format";

const WARNINGS = ["Duplicate tracking ID", "No tracking ID", "Order ID format wrong", "No return date", "No product name", "Refund date missing", "Return type missing", "Reason missing"];
const all = (label, list) => [["all", label], ...list.map((x) => [x, x])];
const F = ({ value, onChange, ...rest }) => <SimpleSelect value={value || "all"} onChange={(v) => onChange(v === "all" ? "" : v)} {...rest} />;

export function ReturnsRegister() {
  const meta = useRetMeta();
  const qs = new URLSearchParams(window.location.search);
  const [p, setP] = usePref("returns.register.filters", { q: "", status: "", next_action: "", courier: "", channel: "", return_type: "", warning: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const params = { ...p, product_id: qs.get("product_id") || "", page };
  const { data } = useLoad(() => api.get("/returns", { params }).then((r) => r.data), [JSON.stringify(params)]);
  const set = (k) => (v) => { setPage(1); setP({ ...p, [k]: v }); };
  if (!meta) return <Loading />;
  const st = meta.settings;
  return (
    <div data-testid="returns-register">
      <PageHeader title="Returns Register" subtitle="Every returned parcel. Search by return ID, order, invoice, tracking or product.">
        <DownloadButton path="/returns-export" params={params} testid="ret-export-btn" label="Export Excel" />
        <Button asChild size="sm"><Link to="/returns/new" data-testid="ret-new-btn"><Plus className="h-4 w-4" /> New return</Link></Button>
      </PageHeader>
      {qs.get("product_id") && <div className="mb-2 text-xs">Filtered to one product (last 90 days view from Product Master) · <Link to="/returns/register" className="text-primary underline">clear</Link></div>}
      <div className="panel mb-3 flex flex-wrap items-center gap-2 p-3">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={p.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search…" className="h-9 w-64 pl-8" data-testid="ret-search" /></div>
        <F value={p.status} onChange={set("status")} options={all("All statuses", meta.statuses)} testid="ret-f-status" />
        <F value={p.next_action} onChange={set("next_action")} options={all("All next actions", meta.actions.map((a) => a.action))} testid="ret-f-action" />
        <F value={p.courier} onChange={set("courier")} options={all("All couriers", opts(meta, "courier"))} testid="ret-f-courier" className="h-9 w-36" />
        <F value={p.channel} onChange={set("channel")} options={all("All channels", opts(meta, "channel"))} testid="ret-f-channel" className="h-9 w-36" />
        <F value={p.return_type} onChange={set("return_type")} options={all("All types", opts(meta, "return_type"))} testid="ret-f-type" />
        <F value={p.warning} onChange={set("warning")} options={all("All data checks", WARNINGS)} testid="ret-f-warning" />
        <Input type="date" value={p.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 w-36" data-testid="ret-f-from" />
        <Input type="date" value={p.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 w-36" data-testid="ret-f-to" />
      </div>
      {!data ? <Loading /> : <>
        <div className="panel overflow-x-auto">
          <table className="data-table" data-testid="ret-table">
            <thead><tr><th>Return ID</th><th>Date</th><th>Channel</th><th>Order / Invoice</th><th>Tracking ID</th><th>Courier</th><th>Product</th><th>Return type</th><th>Status</th><th>Next action</th><th className="text-right">Days open</th><th>Checks</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.id} data-testid={`ret-row-${r.return_id}`}>
                <td><RetLink r={r} /></td><td className="text-xs">{dateLabel(r.return_date)}</td><td className="text-xs">{r.channel}</td>
                <td className="font-mono text-xs">{orderOf(r)}</td><td className="font-mono text-xs">{r.tracking_id}</td><td className="text-xs">{r.courier}</td>
                <td><div className="max-w-[220px] truncate text-xs" title={r.product_name}>{r.product_name}</div></td><td className="text-xs">{r.return_type}</td>
                <td><RetPill status={r.status} /></td><td className="text-xs">{r.next_action}</td>
                <td className="num"><DaysCell days={r.days_open} amber={st.amber_days} red={st.red_days} /></td><td><WarnChips list={r.warnings} /></td>
              </tr>))}</tbody>
          </table>
          {!data.rows.length && <div className="py-10 text-center text-sm text-muted-foreground">No returns match.</div>}
        </div>
        <div className="mt-3 flex items-center justify-between text-sm" data-testid="ret-pager">
          <span className="text-muted-foreground">{fmtNum(data.total)} returns · page {data.page} of {data.pages}</span>
          <div className="flex gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="ret-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="ret-next"><ChevronRight className="h-4 w-4" /></Button></div>
        </div>
      </>}
    </div>
  );
}

export function ReturnDetail() {
  const { id } = useParams();
  const meta = useRetMeta();
  const { data, reload } = useLoad(() => (id ? api.get(`/returns/${id}`).then((r) => r.data) : Promise.resolve(null)), [id]);
  if (!meta || (id && !data)) return <Loading />;
  return (
    <div data-testid="return-detail">
      <PageHeader title={id ? "Return detail" : "New return"} subtitle="Only the fields that apply to this return are shown.">
        <Button asChild size="sm" variant="outline"><Link to="/returns/register">Back to register</Link></Button>
      </PageHeader>
      <ReturnForm meta={meta} initial={data} onSaved={id ? () => reload() : undefined} />
    </div>
  );
}
